import React from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import ReactSelect from 'react-select';
import Draggable from 'react-draggable';
import { Resizable } from 'react-resizable';

import DashboardHeader from '../DashboardModal/components/DashboardHeader';
import UIDateFormater, { DateType } from '../../containers/UIhelper/UIDateFormater';
import { downloadCsv, formatCsvTimestamp, resolveCsvTimeZone } from '../../utils/csvDownload';
import { MAX_SENSORS } from '../../actions/SensorsSamplesActions';

import 'react-resizable/css/styles.css';
import './sensorSamplesReport.scss';

// Identity fields on a /sensorsamples row.
const SENSOR_ID_FIELD = 'SensorID';
const SAMPLE_TIME_FIELD = 'SampleTime'; // epoch ms

// Noise columns rendered after SensorID + SampleTime. Keys match the
// /sensorsamples response fields; labels are i18n keys.
const NOISE_COLUMNS = [
  { key: 'sample_noise', labelKey: 'sample_noise' },
  { key: 'sample_noise_low_freq', labelKey: 'sample_noise_low_freq' },
  { key: 'sample_noise_med_freq', labelKey: 'sample_noise_med_freq' },
  { key: 'sample_noise_high_freq', labelKey: 'sample_noise_high_freq' },
  { key: 'min_noise', labelKey: 'min_noise' },
  { key: 'min_noise_low_freq', labelKey: 'min_noise_low_freq' },
  { key: 'min_noise_med_freq', labelKey: 'min_noise_med_freq' },
  { key: 'min_noise_high_freq', labelKey: 'min_noise_high_freq' },
];

const FONT_FAMILY = 'Helvetica, sans-serif';
const TEXT_COLOR = '#333';
const BORDER_COLOR = '#e0e0e0';
const SUBTLE_BG = '#f7f7f7';

const rootStyle = { fontFamily: FONT_FAMILY, color: TEXT_COLOR, fontSize: '13px' };

const filterBarStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '10px 12px',
  marginBottom: '12px',
  background: SUBTLE_BG,
  border: `1px solid ${BORDER_COLOR}`,
  borderRadius: '4px',
  flexWrap: 'wrap',
  flexShrink: 0,
};

const labelStyle = {
  fontSize: '12px',
  fontWeight: 600,
  color: TEXT_COLOR,
  marginRight: '6px',
  fontFamily: FONT_FAMILY,
};

const inputStyle = {
  fontSize: '13px',
  padding: '4px 8px',
  border: `1px solid ${BORDER_COLOR}`,
  borderRadius: '4px',
  fontFamily: FONT_FAMILY,
  color: TEXT_COLOR,
  background: '#fff',
  height: '30px',
  boxSizing: 'border-box',
};

// Fixed-height control: selected chips wrap and scroll inside a constant-height
// box so adding/removing sensors never reflows the surrounding filter bar.
const SELECT_HEIGHT = 56;
const sensorSelectStyles = {
  container: (base) => ({ ...base, minWidth: '280px', flex: '1 1 auto' }),
  control: (base) => ({
    ...base,
    height: SELECT_HEIGHT,
    minHeight: SELECT_HEIGHT,
    borderColor: BORDER_COLOR,
    fontFamily: FONT_FAMILY,
    fontSize: '13px',
    flexWrap: 'nowrap',
    alignItems: 'stretch',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '4px 6px',
    height: SELECT_HEIGHT,
    maxHeight: SELECT_HEIGHT,
    overflowY: 'auto',
    overflowX: 'hidden',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    alignItems: 'flex-start',
  }),
  indicatorsContainer: (base) => ({ ...base, alignItems: 'flex-start' }),
  multiValue: (base) => ({ ...base, background: '#e8f0f5' }),
  multiValueLabel: (base) => ({ ...base, color: TEXT_COLOR, fontSize: '12px' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const tableHeaderCellStyle = {
  padding: '8px 10px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  color: '#666',
  borderBottom: `1px solid ${BORDER_COLOR}`,
  background: SUBTLE_BG,
  verticalAlign: 'middle',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  fontFamily: FONT_FAMILY,
};

const tableCellStyle = {
  padding: '8px 10px',
  fontSize: '13px',
  borderBottom: `1px solid ${BORDER_COLOR}`,
  verticalAlign: 'middle',
  fontFamily: FONT_FAMILY,
  color: TEXT_COLOR,
  whiteSpace: 'nowrap',
};

const getDefaultDates = () => {
  const today = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: today.toISOString().split('T')[0],
  };
};

// Samples can only be queried up to one year back from today.
const getMinStartDate = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().split('T')[0];
};

const renderNumber = (v) => (v == null || v === '' ? '-' : v);

// Some shared i18n values carry a trailing ":" (e.g. sample_time = "Sample Time:")
// since they're reused as inline labels — strip it for table / CSV column headers.
const stripColon = (label) => (label || '').replace(/:\s*$/, '');

const isEmptyCell = (v) => v == null || v === '';

// Compare two raw cell values numerically when both look numeric, else as locale
// strings. Emptiness is handled by the caller so it can sink blanks consistently.
const compareCellValues = (va, vb) => {
  const na = Number(va);
  const nb = Number(vb);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na < nb ? -1 : na > nb ? 1 : 0;
  return String(va).localeCompare(String(vb), undefined, { numeric: true });
};

// Build a row comparator for the active column. Empty cells always sink to the
// bottom (regardless of direction); SensorID then SampleTime act as ascending
// tiebreakers so rows stay grouped predictably no matter which column drives the
// sort. Sorting by SensorID asc reproduces the original default ordering.
const buildRowComparator = (column, direction) => {
  const dir = direction === 'desc' ? -1 : 1;
  return (a, b) => {
    const aEmpty = isEmptyCell(a[column]);
    const bEmpty = isEmptyCell(b[column]);
    if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
    if (!aEmpty) {
      const primary = compareCellValues(a[column], b[column]) * dir;
      if (primary !== 0) return primary;
    }
    if (column !== SENSOR_ID_FIELD) {
      const c = compareCellValues(a[SENSOR_ID_FIELD], b[SENSOR_ID_FIELD]);
      if (c !== 0) return c;
    }
    if (column !== SAMPLE_TIME_FIELD) {
      return compareCellValues(a[SAMPLE_TIME_FIELD], b[SAMPLE_TIME_FIELD]);
    }
    return 0;
  };
};

export default class SensorSamplesReportDialog extends React.Component {
  static get propTypes() {
    return {
      open: PropTypes.bool.isRequired,
      onClose: PropTypes.func.isRequired,
      projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      timeZone: PropTypes.string,
      projectTimezone: PropTypes.string,
      sensorOptions: PropTypes.array,
      initialSensorIds: PropTypes.array,
      fetchSensorSamples: PropTypes.func.isRequired,
    };
  }

  constructor(props) {
    super(props);
    const { startDate, endDate } = getDefaultDates();
    this.state = {
      startDate,
      endDate,
      selectedSensorIds: this.filterToOptions(props.initialSensorIds),
      rows: [],
      sortColumn: SENSOR_ID_FIELD,
      sortDirection: 'asc',
      isFetching: false,
      hasLoaded: false,
      error: null,
      ...this.getInitialLayout(),
      isMaximized: false,
    };
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  componentDidUpdate(prevProps) {
    // Reset when the dialog transitions from closed to open.
    if (this.props.open && !prevProps.open) {
      const { startDate, endDate } = getDefaultDates();
      this.setState({
        startDate,
        endDate,
        selectedSensorIds: this.filterToOptions(this.props.initialSensorIds),
        rows: [],
        sortColumn: SENSOR_ID_FIELD,
        sortDirection: 'asc',
        isFetching: false,
        hasLoaded: false,
        error: null,
        ...this.getInitialLayout(),
        isMaximized: false,
      });
    }
  }

  // Keep only ids that exist among the acoustic sensor options (drops pressure /
  // unknown ids that may arrive from a polygon selection), capped at MAX_SENSORS.
  filterToOptions = (ids) => {
    const optionValues = new Set((this.props.sensorOptions || []).map((o) => o.value));
    return (ids || [])
      .map(String)
      .filter((id) => optionValues.has(id))
      .slice(0, MAX_SENSORS);
  };

  getInitialLayout = () => {
    const width = Math.min(1100, Math.round(window.innerWidth * 0.9));
    const height = Math.min(680, Math.round(window.innerHeight * 0.85));
    return {
      size: { width, height },
      position: { x: Math.max(0, Math.round((window.innerWidth - width) / 2)), y: 40 },
    };
  };

  handleKeyDown = (e) => {
    if (e.key === 'Escape' && this.props.open) {
      this.props.onClose();
    }
  };

  handleDrag = (_, data) => {
    if (!this.state.isMaximized) {
      this.setState({ position: { x: data.x, y: data.y } });
    }
  };

  handleResize = (_, { size: newSize }) => {
    if (!this.state.isMaximized) {
      this.setState({ size: { width: newSize.width, height: newSize.height } });
    }
  };

  toggleMaximize = () => this.setState((s) => ({ isMaximized: !s.isMaximized }));

  getSelectedOptions = () => {
    const { sensorOptions } = this.props;
    const { selectedSensorIds } = this.state;
    return (sensorOptions || []).filter((opt) => selectedSensorIds.includes(opt.value));
  };

  handleSensorSelectionChange = (selected) => {
    const ids = (selected || []).map((opt) => opt.value).slice(0, MAX_SENSORS);
    this.setState({ selectedSensorIds: ids });
  };

  handleLoad = () => {
    const { startDate, endDate, selectedSensorIds } = this.state;
    const { projectId, fetchSensorSamples } = this.props;

    if (!startDate || !endDate) {
      this.setState({ error: 'Please select a start and end date' });
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      this.setState({ error: 'End date must be on or after the start date' });
      return;
    }
    if (new Date(startDate) < new Date(getMinStartDate())) {
      this.setState({ error: 'Start date cannot be more than one year ago' });
      return;
    }
    if (!selectedSensorIds.length) {
      this.setState({ error: this.context.t('select') });
      return;
    }

    this.setState({ isFetching: true, error: null });
    fetchSensorSamples(projectId, selectedSensorIds, startDate, endDate)
      .then((rows) => {
        this.setState({ rows: rows || [], isFetching: false, hasLoaded: true });
      })
      .catch(() => {
        this.setState({ rows: [], isFetching: false, hasLoaded: true, error: 'Failed to fetch samples' });
      });
  };

  getSortedRows = () => {
    const { rows, sortColumn, sortDirection } = this.state;
    return [...(rows || [])].sort(buildRowComparator(sortColumn, sortDirection));
  };

  // Click a header to sort by it: same column toggles asc/desc, a new column
  // starts ascending.
  handleSort = (columnKey) => {
    this.setState((s) => (
      s.sortColumn === columnKey
        ? { sortDirection: s.sortDirection === 'asc' ? 'desc' : 'asc' }
        : { sortColumn: columnKey, sortDirection: 'asc' }
    ));
  };

  handleDownloadCsv = () => {
    const { timeZone, projectTimezone } = this.props;
    const { startDate, endDate } = this.state;
    const { ianaTimeZone, label: tzLabel } = resolveCsvTimeZone(timeZone, projectTimezone);
    const t = this.context.t;

    const headers = [
      stripColon(t('sensor_id')),
      `${stripColon(t('sample_time'))} (${tzLabel})`,
      ...NOISE_COLUMNS.map((c) => stripColon(t(c.labelKey))),
    ];
    const rows = this.getSortedRows().map((row) => [
      row[SENSOR_ID_FIELD],
      formatCsvTimestamp(row[SAMPLE_TIME_FIELD], ianaTimeZone),
      ...NOISE_COLUMNS.map((c) => (row[c.key] == null ? '' : row[c.key])),
    ]);

    downloadCsv(`sensor_samples_report_${startDate}_to_${endDate}.csv`, headers, rows);
  };

  // Clickable, sort-aware header cell. Shows a faint neutral indicator on
  // inactive columns for discoverability and a highlighted arrow on the active one.
  renderHeaderCell = (columnKey, labelKey) => {
    const t = this.context.t;
    const { sortColumn, sortDirection } = this.state;
    const isActive = sortColumn === columnKey;
    const indicator = isActive ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅';
    return (
      <th
        key={columnKey}
        style={{ ...tableHeaderCellStyle, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        onClick={() => this.handleSort(columnKey)}
        aria-sort={isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        {stripColon(t(labelKey))}
        <span style={{ marginLeft: '4px', color: isActive ? '#1976d2' : '#bbb', fontSize: '11px' }}>{indicator}</span>
      </th>
    );
  };

  renderTable = () => {
    const { timeZone, projectTimezone } = this.props;
    const rows = this.getSortedRows();

    return (
      <div style={{ flex: '1 1 auto', overflowY: 'auto', overflowX: 'auto', border: `1px solid ${BORDER_COLOR}`, borderRadius: '4px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT_FAMILY }}>
          <thead>
            <tr>
              {this.renderHeaderCell(SENSOR_ID_FIELD, 'sensor_id')}
              {this.renderHeaderCell(SAMPLE_TIME_FIELD, 'sample_time')}
              {NOISE_COLUMNS.map((c) => this.renderHeaderCell(c.key, c.labelKey))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={`${row[SENSOR_ID_FIELD]}-${row[SAMPLE_TIME_FIELD]}-${idx}`}>
                <td style={{ ...tableCellStyle, fontWeight: 600 }}>{row[SENSOR_ID_FIELD]}</td>
                <td style={tableCellStyle}>
                  {UIDateFormater(row[SAMPLE_TIME_FIELD], DateType.DATE_AND_TIME, timeZone, projectTimezone)}
                </td>
                {NOISE_COLUMNS.map((c) => (
                  <td key={c.key} style={tableCellStyle}>{renderNumber(row[c.key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  render() {
    const { open, onClose, sensorOptions } = this.props;
    if (!open) return null;

    const { startDate, endDate, selectedSensorIds, isFetching, hasLoaded, error, rows, position, size, isMaximized } = this.state;
    const t = this.context.t;
    const atCap = selectedSensorIds.length >= MAX_SENSORS;

    const content = (
      <div style={{ ...rootStyle, width: '100%', display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
        <div style={filterBarStyle}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={labelStyle}>{t('start_date')}:</span>
            <input type="date" value={startDate} min={getMinStartDate()} max={endDate || undefined}
              onChange={(e) => this.setState({ startDate: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={labelStyle}>{t('end_date')}:</span>
            <input type="date" value={endDate} min={startDate || undefined}
              onChange={(e) => this.setState({ endDate: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', flex: '1 1 320px' }}>
            <span style={labelStyle}>{t('sensors')}:</span>
            <ReactSelect
              isMulti
              closeMenuOnSelect={false}
              options={sensorOptions}
              value={this.getSelectedOptions()}
              onChange={this.handleSensorSelectionChange}
              placeholder={t('select')}
              menuPortalTarget={document.body}
              styles={sensorSelectStyles}
              isOptionDisabled={() => atCap}
            />
          </div>
          {atCap && <span style={{ fontSize: '11px', color: '#888' }}>{t('max_15_sensors')}</span>}
          <Button variant="contained" color="primary" size="small"
            onClick={this.handleLoad} disabled={isFetching || !selectedSensorIds.length}>
            {t('load')}
          </Button>
          <Button variant="outlined" color="primary" size="small"
            onClick={this.handleDownloadCsv} disabled={isFetching || !rows.length}>
            {`${t('download')} CSV`}
          </Button>
        </div>

        {error && <div style={{ color: 'red', marginBottom: '8px', fontSize: '0.875rem' }}>{error}</div>}

        {rows.length ? (
          this.renderTable()
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0', fontFamily: FONT_FAMILY }}>
            <p style={{ color: '#666', margin: 0 }}>
              {isFetching ? t('loading') : (hasLoaded ? t('no_samples_found') : t('select'))}
            </p>
          </div>
        )}
      </div>
    );

    const headerProps = {
      onClose,
      onMaximize: this.toggleMaximize,
      isMaximized,
      title: t('samples_report'),
      showMinimize: false,
      t,
    };

    if (isMaximized) {
      return (
        <div className="sensor-samples-report-modal-overlay maximized">
          <Paper className="sensor-samples-report-modal maximized">
            <DashboardHeader {...headerProps} />
            <div className="sensor-samples-report-modal-content">{content}</div>
          </Paper>
        </div>
      );
    }

    return (
      <div className="sensor-samples-report-modal-overlay">
        <Draggable handle=".dashboard-header" position={position} onDrag={this.handleDrag} bounds="parent">
          <Resizable
            width={size.width}
            height={size.height}
            onResize={this.handleResize}
            minConstraints={[640, 360]}
            maxConstraints={[Math.round(window.innerWidth * 0.98), Math.round(window.innerHeight * 0.98)]}
            resizeHandles={['se', 'e', 's']}
          >
            <Paper className="sensor-samples-report-modal" style={{ width: size.width, height: size.height }}>
              <DashboardHeader {...headerProps} />
              <div className="sensor-samples-report-modal-content">{content}</div>
            </Paper>
          </Resizable>
        </Draggable>
      </div>
    );
  }
}

SensorSamplesReportDialog.contextTypes = {
  t: PropTypes.func.isRequired,
};
